import React, { useState } from "react";
import { Grid } from "@material-ui/core";

import Api from "../../services/api";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import swal from "sweetalert";

const image_formats = ["image/jpg", "image/jpeg", "image/png", "image/webp"];

// Regra do upload em um lugar só, para o texto exibido e a validação nunca divergirem.
const MAX_FILE_MB = 2;
const MAX_FILE_BYTES = MAX_FILE_MB * 1000000;
const ACCEPT_ATTR = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";
const UPLOAD_HINT = `Formatos aceitos: PNG, JPG, JPEG ou WebP — no máximo ${MAX_FILE_MB} MB.`;

// O site mostra a imagem inteira, sem corte: a proporção enviada é a que aparece.
const MEDIDAS = {
  topo: {
    imagem: "Sugestão: 1600 × 400 px (retangular largo).",
    imagem_mobile: "Sugestão: 800 × 600 px.",
  },
  cards: {
    imagem: "Sugestão: 1200 × 1200 px (quadrado) ou 1600 × 900 px (retangular).",
    imagem_mobile: "Sugestão: 1200 × 1200 px (quadrado) ou 1200 × 900 px (retangular).",
  },
};

const dicaProporcao = (section, campo) => {
  const medida = (MEDIDAS[section] || MEDIDAS.cards)[campo];
  const extra =
    section === "cards"
      ? " Use a mesma proporção nos dois cards para eles ficarem iguais lado a lado."
      : "";

  return `A imagem aparece inteira, sem corte: a proporção que você enviar é a que vai aparecer no site. ${medida}${extra}`;
};
const ERRO_TAMANHO = `Imagem acima de ${MAX_FILE_MB} MB. Selecione uma imagem menor.`;
const ERRO_FORMATO = "Formato inválido. Use PNG, JPG, JPEG ou WebP.";

const fileTest = (file) => {
  if (file && file.length === 1) {
    return file[0].size <= MAX_FILE_BYTES;
  }
  return true;
};
const formatTest = (file) => {
  if (file && file.length === 1) {
    return image_formats.includes(file[0].type);
  }
  return true;
};

const validationSchema = yup.object().shape({
  section: yup.string().required("Seção obrigatória"),
  destino: yup.string().when("section", {
    is: "cards",
    then: (schema) => schema.required("Destino obrigatório"),
    otherwise: (schema) => schema.nullable(),
  }),
  idioma: yup.string().required("Idioma obrigatório"),
  imagem: yup
    .mixed()
    .test("required", "Imagem (desktop) obrigatória", (file) => file && file.length === 1)
    .test("fileSize", ERRO_TAMANHO, fileTest)
    .test("FILE_FORMAT", ERRO_FORMATO, formatTest),
  imagem_mobile: yup
    .mixed()
    .test("fileSize", ERRO_TAMANHO, fileTest)
    .test("FILE_FORMAT", ERRO_FORMATO, formatTest),
  link: yup.string().nullable(),
  ordem: yup.number().typeError("Ordem deve ser número").integer().min(0),
});

export default function NewBanner({ loadDestinos, setOpenModalNew }) {
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("topo");
  const Loader = () => <div className="loading"></div>;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = (formData) => {
    setLoading(true);
    const imagefile = document.querySelector("#imagem");
    const imagefileMobile = document.querySelector("#imagem_mobile");

    const destino = formData.section === "topo" ? "main" : formData.destino;
    const bodyFormData = new FormData();
    bodyFormData.append("section", formData.section);
    bodyFormData.append("destino", destino);
    bodyFormData.append("idioma", formData.idioma);
    bodyFormData.append("link", formData.link || "");
    bodyFormData.append("ordem", formData.ordem || 0);
    if (imagefile.files.length > 0) {
      bodyFormData.append("imagem", imagefile.files[0]);
    }
    if (imagefileMobile.files.length > 0) {
      bodyFormData.append("imagem_mobile", imagefileMobile.files[0]);
    }

    const axiosConfig = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    Api.post("/destinoshome/new", bodyFormData, axiosConfig)
      .then((response) => {
        if (response.data.status === "success") {
          swal("Sucesso!", response.data.message, "success");
          loadDestinos();
          setOpenModalNew(false);
        } else {
          swal("Erro!", response.data.message, "error");
        }
        setLoading(false);
      })
      .catch((err) => {
        swal("Erro!", "ops! ocorreu um erro: " + err, "error");
        setLoading(false);
      });
  };

  return (
    <>
      {loading ? <Loader /> : null}
      <h2>Novo Banner</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className="form">
          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Seção (onde o banner aparece na home)</label>
            <select
              {...register("section")}
              value={section}
              onChange={(e) => setSection(e.target.value)}
            >
              <option value="topo">Banner do Topo (acima dos cards)</option>
              <option value="cards">Card de Destino</option>
            </select>
            <span className="error">{errors.section?.message}</span>
          </Grid>

          {section === "cards" && (
            <Grid item lg={12} md={12} sm={12} sx={12} className="field">
              <label>Destino</label>
              <select {...register("destino")} defaultValue="">
                <option value="" disabled>
                  Selecione...
                </option>
                <option value="ilhabela">Ilhabela / SP</option>
                <option value="paraty">Paraty e Ilha Grande / RJ</option>
              </select>
              <span className="error">{errors.destino?.message}</span>
            </Grid>
          )}

          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Idioma</label>
            <select {...register("idioma")} defaultValue="">
              <option value="" disabled>
                Selecione...
              </option>
              <option value="pt">Português</option>
              <option value="en">Inglês</option>
              <option value="es">Espanhol</option>
            </select>
            <span className="error">{errors.idioma?.message}</span>
          </Grid>

          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Imagem (desktop)</label>
            <span className="hint">{UPLOAD_HINT}</span>
            <span className="hint">{dicaProporcao(section, "imagem")}</span>
            <input
              type="file"
              name="imagem"
              id="imagem"
              accept={ACCEPT_ATTR}
              {...register("imagem")}
            />
            <span className="error">{errors.imagem?.message}</span>
          </Grid>

          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Imagem (mobile — opcional)</label>
            <span className="hint">{UPLOAD_HINT}</span>
            <span className="hint">{dicaProporcao(section, "imagem_mobile")}</span>
            <input
              type="file"
              name="imagem_mobile"
              id="imagem_mobile"
              accept={ACCEPT_ATTR}
              {...register("imagem_mobile")}
            />
            <span className="error">{errors.imagem_mobile?.message}</span>
          </Grid>

          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Link (destino do clique — use URL completa para externo)</label>
            <input type="text" {...register("link")} placeholder="https://..." />
            <span className="error">{errors.link?.message}</span>
          </Grid>

          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Ordem (menor aparece primeiro no carrossel)</label>
            <input type="number" {...register("ordem")} defaultValue={0} />
            <span className="error">{errors.ordem?.message}</span>
          </Grid>
        </Grid>
        <div className="actions">
          <button type="submit" className="save">
            Salvar
          </button>
          <a
            href="#"
            className="cancel"
            onClick={(e) => {
              e.preventDefault();
              setOpenModalNew(false);
            }}
          >
            Cancelar
          </a>
        </div>
      </form>
    </>
  );
}
